import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { AuthenticationGuard } from "./components/AuthenticationGuard";
import { LogoutButton } from "./components/LogoutButton";
import { CreateMesocycle } from "./components/CreateMesocycle";
import { MesocycleDetail } from "./components/MesocycleDetail";
import { MesocycleList } from "./components/MesocycleList";
import { SessionView } from "./components/SessionView";
import { useMesocycles } from "./hooks/useMesocycles";
import { setAccessTokenGetter } from "./services/api";
import { Mesocycle, Session } from "./types";

type View =
  | { type: "list" }
  | { type: "detail"; mesocycle: Mesocycle }
  | {
      type: "session";
      mesocycle: Mesocycle;
      session: Session;
      weekNumber: number;
    }
  | { type: "create" };

function App() {
  const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [currentView, setCurrentView] = useState<View>({ type: "list" });
  const { mesocycles, loading, error, createMesocycle, deleteMesocycle } = useMesocycles(isAuthenticated);

  useEffect(() => {
    // Set up the access token getter for API calls
    setAccessTokenGetter(async () => {
      try {
        const token = await getAccessTokenSilently();
        return token;
      } catch (error) {
        console.error('Error getting access token:', error);
        throw error;
      }
    });
  }, [getAccessTokenSilently]);

  const handleCreateMesocycle = async (mesocycle: Omit<Mesocycle, "id">) => {
    try {
      await createMesocycle(mesocycle);
      setCurrentView({ type: "list" });
    } catch (err) {
      alert("Failed to create mesocycle. Please try again.");
    }
  };

  const handleDeleteMesocycle = async (id: string) => {
    try {
      await deleteMesocycle(id);
      setCurrentView({ type: "list" });
    } catch (err) {
      alert("Failed to delete mesocycle. Please try again.");
    }
  };

  const renderView = () => {
    switch (currentView.type) {
      case "list":
        return (
          <div className="min-h-screen bg-gray-50">
            <div className="bg-white shadow-sm">
              <div className="px-4 py-4 flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Shred Log</h1>
                  {user && (
                    <p className="text-sm text-gray-600 mt-1">
                      Welcome, {user.name || user.email}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <LogoutButton />
                  <button
                    onClick={() => setCurrentView({ type: "create" })}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    New Mesocycle
                  </button>
                </div>
              </div>
            </div>
            <div className="px-4 py-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Active Mesocycles
              </h2>
              {loading && (
                <div className="text-center text-gray-600">Loading...</div>
              )}
              {error && (
                <div className="text-center text-red-600 p-4 bg-red-50 rounded-lg">
                  {error}
                </div>
              )}
              {!loading && !error && (
                <MesocycleList
                  mesocycles={mesocycles}
                  onSelectMesocycle={(mesocycle) =>
                    setCurrentView({ type: "detail", mesocycle })
                  }
                />
              )}
            </div>
          </div>
        );

      case "detail":
        return (
          <MesocycleDetail
            mesocycle={currentView.mesocycle}
            onBack={() => setCurrentView({ type: "list" })}
            onSelectSession={(session, weekNumber) =>
              setCurrentView({
                type: "session",
                mesocycle: currentView.mesocycle,
                session,
                weekNumber,
              })
            }
            onDelete={handleDeleteMesocycle}
          />
        );

      case "session":
        return (
          <SessionView
            mesocycle={currentView.mesocycle}
            session={currentView.session}
            weekNumber={currentView.weekNumber}
            onBack={() =>
              setCurrentView({
                type: "detail",
                mesocycle: currentView.mesocycle,
              })
            }
          />
        );

      case "create":
        return (
          <CreateMesocycle
            onCancel={() => setCurrentView({ type: "list" })}
            onCreate={handleCreateMesocycle}
          />
        );
    }
  };

  return (
    <AuthenticationGuard>
      <div className="App">{renderView()}</div>
    </AuthenticationGuard>
  );
}

export default App;
